import React, { useEffect, useRef, useState } from 'react';

const CyberBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    // Ajustar el tamaño del canvas cuando la ventana cambia de tamaño
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    // Definir nodos y conexiones
    let nodes: { x: number; y: number; vx: number; vy: number; radius: number; color: string }[] = [];
    const nodeCount = Math.floor(dimensions.width * dimensions.height / 25000); // Ajustar según densidad deseada

    // Crear nodos aleatorios
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * dimensions.width,
        y: Math.random() * dimensions.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 2 + 1,
        color: Math.random() > 0.95 ? '#FF3B3F' : '#00A8E8'
      });
    }

    // Crear puntos de ataque (nodos rojos más grandes que parpadean)
    const attackPoints = 3;
    for (let i = 0; i < attackPoints; i++) {
      nodes.push({
        x: Math.random() * dimensions.width,
        y: Math.random() * dimensions.height,
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        radius: 4,
        color: '#FF3B3F'
      });
    }

    // Función para dibujar el fondo de ciberseguridad
    const draw = () => {
      // Fondo oscuro
      ctx.fillStyle = '#051937';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Dibujar una cuadrícula sutil
      ctx.strokeStyle = 'rgba(0, 168, 232, 0.1)';
      ctx.lineWidth = 0.3;
      const gridSize = 50;
      
      for (let x = 0; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      
      for (let y = 0; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Actualizar y dibujar nodos
      nodes.forEach((node, index) => {
        // Mover nodos
        node.x += node.vx;
        node.y += node.vy;

        // Rebotar en los bordes
        if (node.x < 0 || node.x > dimensions.width) node.vx *= -1;
        if (node.y < 0 || node.y > dimensions.height) node.vy *= -1;

        // Efecto de parpadeo para los nodos de ataque (rojos grandes)
        if (node.radius > 3) {
          const pulseSize = 2 + Math.sin(Date.now() * 0.005) * 2;
          const pulseOpacity = 0.4 + Math.sin(Date.now() * 0.005) * 0.3;
          
          // Dibujar aura del ataque
          ctx.beginPath();
          ctx.arc(node.x, node.y, pulseSize * 5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 59, 63, ${pulseOpacity * 0.1})`;
          ctx.fill();
          
          ctx.beginPath();
          ctx.arc(node.x, node.y, pulseSize * 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 59, 63, ${pulseOpacity * 0.2})`;
          ctx.fill();
        }

        // Dibujar nodo
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = node.color;
        ctx.fill();

        // Dibujar conexiones entre nodos cercanos
        nodes.forEach((otherNode, otherIndex) => {
          if (index !== otherIndex) {
            const dx = node.x - otherNode.x;
            const dy = node.y - otherNode.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < 100) {
              ctx.beginPath();
              ctx.moveTo(node.x, node.y);
              ctx.lineTo(otherNode.x, otherNode.y);
              
              // Si uno de los nodos es rojo (ataque), la conexión es roja
              if (node.color === '#FF3B3F' || otherNode.color === '#FF3B3F') {
                ctx.strokeStyle = `rgba(255, 59, 63, ${0.8 - distance / 120})`;
              } else {
                ctx.strokeStyle = `rgba(0, 168, 232, ${0.5 - distance / 200})`;
              }
              
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        });
      });

      // Efecto de flujo de datos (líneas verticales que se mueven)
      for (let i = 0; i < 20; i++) {
        const x = Math.sin(Date.now() * 0.001 + i) * dimensions.width/2 + dimensions.width/2;
        const height = 20 + Math.random() * 100;
        const y = (Date.now() * 0.05 + i * 100) % dimensions.height;
        
        ctx.fillStyle = 'rgba(0, 168, 232, 0.2)';
        ctx.fillRect(x, y, 1, height);
      }

      requestAnimationFrame(draw);
    };

    // Iniciar la animación
    draw();
  }, [dimensions]);

  return (
    <canvas 
      ref={canvasRef} 
      className="fixed top-0 left-0 w-full h-full -z-10"
    />
  );
};

export default CyberBackground;